import React, { useState } from 'react';
import { useStore } from '../context/ProductStore';

/**
 * QuoteRequestModal — the customer-facing "Request a Quotation" form.
 *
 * Customers do NOT build a priced quote themselves. They just tell us
 * what they need and how to reach them; the request is saved to
 * Firestore (quoteRequests) so it shows up for the admin from ANY
 * device, and staff follow up directly (WhatsApp/call/email) or build
 * a formal priced quotation from the Admin Panel.
 */
const QuoteRequestModal = ({ onClose }) => {
  const { submitQuoteRequest } = useStore();

  const [form, setForm] = useState({ name: '', phone: '', email: '', budget: '', requirements: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.phone.trim() || !form.requirements.trim()) {
      setError('Please fill in your name, phone number, and what you\'re looking for.');
      return;
    }
    setSubmitting(true);
    try {
      await submitQuoteRequest(form);
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong sending your request. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div>
            <h2 style={S.title}>📄 Request a Quotation</h2>
            <p style={S.subtitle}>Tell us what you need — we'll get back to you with pricing.</p>
          </div>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>

        {submitted ? (
          <div style={S.successBox}>
            <div style={{ fontSize: 40 }}>✅</div>
            <div style={S.successTitle}>Request sent!</div>
            <div style={S.successText}>
              Thanks, {form.name.split(' ')[0]}. Our team will review your requirements and get back to you
              on {form.phone} shortly.
            </div>
            <button onClick={onClose} style={S.doneBtn}>Done</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={S.form}>
            {error && <div style={S.errorBox}>{error}</div>}

            <div style={S.field}>
              <label style={S.label}>Your Name *</label>
              <input style={S.input} value={form.name} onChange={update('name')} placeholder="e.g. Samuel Marufu" />
            </div>

            <div style={S.row2}>
              <div style={S.field}>
                <label style={S.label}>Phone Number *</label>
                <input style={S.input} value={form.phone} onChange={update('phone')} placeholder="e.g. 077 123 4567" />
              </div>
              <div style={S.field}>
                <label style={S.label}>Email (optional)</label>
                <input style={S.input} type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" />
              </div>
            </div>

            <div style={S.field}>
              <label style={S.label}>Budget (optional)</label>
              <input style={S.input} value={form.budget} onChange={update('budget')} placeholder="e.g. USD 500 – 800" />
            </div>

            <div style={S.field}>
              <label style={S.label}>What are you looking for? *</label>
              <textarea
                style={S.textarea}
                rows={5}
                value={form.requirements}
                onChange={update('requirements')}
                placeholder="e.g. A laptop for graphic design work, at least 16GB RAM, good battery life, needed by end of month."
              />
            </div>

            <button type="submit" disabled={submitting} style={S.submitBtn(submitting)}>
              {submitting ? 'Sending…' : 'Send Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9998,
    backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  },
  modal: {
    backgroundColor: '#fff', borderRadius: 16, width: '100%', maxWidth: 480,
    maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '20px 24px', borderBottom: '1px solid #f0f0f0',
  },
  title: { fontSize: 18, fontWeight: 800, color: '#111', margin: 0 },
  subtitle: { fontSize: 13, color: '#666', marginTop: 4 },
  closeBtn: {
    width: 32, height: 32, borderRadius: '50%', border: 'none',
    background: '#f3f4f6', color: '#333', cursor: 'pointer', fontSize: 14, flexShrink: 0,
  },
  form: { padding: 24, display: 'flex', flexDirection: 'column', gap: 16 },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 700, color: '#374151' },
  input: {
    padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb',
    fontSize: 14, outline: 'none', fontFamily: 'inherit', color: '#111', background: '#fff',
  },
  textarea: {
    padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb',
    fontSize: 14, outline: 'none', fontFamily: 'inherit', resize: 'vertical', color: '#111', background: '#fff',
  },
  errorBox: {
    background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
    borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#b91c1c',
  },
  submitBtn: (loading) => ({
    padding: '13px', borderRadius: 8, border: 'none',
    background: loading ? '#999' : 'linear-gradient(135deg,#CC0000,#990000)',
    color: '#fff', fontWeight: 700, fontSize: 14, cursor: loading ? 'default' : 'pointer',
    marginTop: 4,
  }),
  successBox: {
    padding: '40px 32px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', textAlign: 'center', gap: 8,
  },
  successTitle: { fontSize: 18, fontWeight: 800, color: '#111' },
  successText: { fontSize: 13.5, color: '#555', lineHeight: 1.6, maxWidth: 340 },
  doneBtn: {
    marginTop: 12, padding: '10px 28px', borderRadius: 8, border: 'none',
    background: '#111', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
  },
};

export default QuoteRequestModal;
